import { useCallback } from 'react';
import { useInstantSearch } from 'react-instantsearch';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { FilterSection } from '@sorare/core/src/components/search/FilterSection';
import { FilterTitle } from '@sorare/core/src/components/search/FilterTitle';
import type { ExtendedUIState } from '@sorare/core/src/components/search/InstantSearch/types';
import { Option } from '@sorare/core/src/components/search/Option';
import { useFacet } from '@sorare/core/src/contexts/graphql-search';
import { useNextSo5FixtureTeamSlugs } from '@sorare/core/src/hooks/config/useNextSo5FixtureTeamSlugs';
import { useVirtualToggle } from '@sorare/core/src/hooks/useVirtualToggle';
import { useVirtualToggleManager } from '@sorare/core/src/hooks/useVirtualToggleManager';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import type { FilterWidget } from '@sorare/core/src/lib/filters';
import { VIRTUAL_TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';

const FILTERS: Record<
  typeof So5FixtureEvent.CLASSIC | typeof So5FixtureEvent.DAILY,
  any
> = {
  [So5FixtureEvent.CLASSIC]: VIRTUAL_TOGGLE_FILTERS.gameweekFilter,
  [So5FixtureEvent.DAILY]: VIRTUAL_TOGGLE_FILTERS.dailyFilter,
};

type Props = {
  visible: boolean;
  eventType: typeof So5FixtureEvent.CLASSIC | typeof So5FixtureEvent.DAILY;
};

const FixtureFilter = ({ visible, eventType }: Props) => {
  const FILTER = FILTERS[eventType];

  const teamsPlayingNextFixture = useNextSo5FixtureTeamSlugs(eventType);
  const setVirtualToggle = useVirtualToggleManager();
  const { currentRefinement } = useVirtualToggle<string>({
    name: FILTER.name,
  });

  const track = useEvents();
  const onChange = useCallback(
    (gw: string) => {
      const isActive = currentRefinement === gw;
      const newValue = isActive ? undefined : gw;
      setVirtualToggle({
        [FILTER.name]: newValue,
      });

      if (!isActive) {
        track('Use Market Filter', {
          filterName: FILTER.trackingName,
          filterValue: gw,
        });
      }
    },
    [
      FILTER.name,
      FILTER.trackingName,
      currentRefinement,
      setVirtualToggle,
      track,
    ]
  );

  if (!visible) return <FilterSection />;

  return (
    <FilterSection>
      {teamsPlayingNextFixture.map(({ gameWeek, longDisplayName, slug }) => (
        <Option
          key={gameWeek}
          active={currentRefinement === slug}
          onClick={() => onChange(slug)}
          variant="radio"
        >
          {longDisplayName}
        </Option>
      ))}
    </FilterSection>
  );
};

const BackendFixtureFilter = ({ visible, eventType }: Props) => {
  const FILTER = FILTERS[eventType];

  const teamsPlayingNextFixture = useNextSo5FixtureTeamSlugs(eventType);
  const {
    refine,
    value: [value],
  } = useFacet(FILTER.name);

  const track = useEvents();
  const onChange = useCallback(
    (gw: string) => {
      const isActive = value === gw;
      const newValue = isActive ? undefined : gw;
      refine(() => (newValue ? [newValue] : []));

      if (!isActive) {
        track('Use Market Filter', {
          filterName: FILTER.trackingName,
          filterValue: gw,
        });
      }
    },
    [FILTER.trackingName, track, refine, value]
  );

  if (!visible) return <FilterSection />;

  return (
    <FilterSection>
      {teamsPlayingNextFixture.map(({ gameWeek, longDisplayName, slug }) => (
        <Option
          key={gameWeek}
          active={value === slug}
          onClick={() => onChange(slug)}
          variant="radio"
        >
          {longDisplayName}
        </Option>
      ))}
    </FilterSection>
  );
};

type TitleProps = {
  eventType: typeof So5FixtureEvent.CLASSIC | typeof So5FixtureEvent.DAILY;
  short?: boolean;
};

const Title = ({ eventType, short }: TitleProps) => {
  const FILTER = FILTERS[eventType];
  const { indexUiState } = useInstantSearch<ExtendedUIState>();
  const selected = !!indexUiState?.virtualToggle?.[FILTER.name];
  return (
    <FilterTitle
      name={
        (short && 'shortTitle' in FILTER && FILTER?.shortTitle) || FILTER.title!
      }
      selected={selected}
    />
  );
};

const BackendTitle = ({ eventType, short }: TitleProps) => {
  const FILTER = FILTERS[eventType];
  const {
    value: [value],
  } = useFacet(FILTER.name);
  return (
    <FilterTitle
      name={
        (short && 'shortTitle' in FILTER && FILTER?.shortTitle) || FILTER.title!
      }
      selected={Boolean(value)}
    />
  );
};

export const GameweekFilter: FilterWidget = {
  key: VIRTUAL_TOGGLE_FILTERS.gameweekFilter.name,
  type: 'virtualToggle',
  filter: VIRTUAL_TOGGLE_FILTERS.gameweekFilter,
  Component: props => (
    <FixtureFilter {...props} eventType={So5FixtureEvent.CLASSIC} />
  ),
  title: <Title eventType={So5FixtureEvent.CLASSIC} />,
  shortTitle: <Title eventType={So5FixtureEvent.CLASSIC} short />,
};

export const BackendGameweekFilter: FilterWidget = {
  key: VIRTUAL_TOGGLE_FILTERS.gameweekFilter.name,
  type: 'virtualToggle',
  filter: VIRTUAL_TOGGLE_FILTERS.gameweekFilter,
  Component: props => (
    <BackendFixtureFilter {...props} eventType={So5FixtureEvent.CLASSIC} />
  ),
  title: <BackendTitle eventType={So5FixtureEvent.CLASSIC} />,
  shortTitle: <BackendTitle eventType={So5FixtureEvent.CLASSIC} short />,
};

export const DailyFilter: FilterWidget = {
  key: VIRTUAL_TOGGLE_FILTERS.dailyFilter.name,
  type: 'virtualToggle',
  filter: VIRTUAL_TOGGLE_FILTERS.dailyFilter,
  Component: props => (
    <FixtureFilter {...props} eventType={So5FixtureEvent.DAILY} />
  ),
  title: <Title eventType={So5FixtureEvent.DAILY} />,
  shortTitle: <Title eventType={So5FixtureEvent.DAILY} short />,
};
